// routes/_authenticated/companies/$cnpj/index.tsx — company detail route. Was
// previously `$cnpj.tsx`; moved here so child routes (itr-evolution, etc.)
// can sit under `/companies/:cnpj/...`. Behavior is unchanged.
import { toDigitsCnpj } from "@shared/utils/cnpj";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { CompanyPage } from "@/features/companies";

type ParsedSearch = { year?: number };

// ponytail: omitting `year` means "every available year" — the backend
// defaults to all years with FCA coverage when the query param is missing.
// The previous all-years sentinel (`year: 0`) leaked into the URL bar
// (`/companies/$cnpj?year=0`); the cleaner shape is no query at all.
function parseSearch(input: Record<string, unknown>): ParsedSearch {
  const raw = input.year;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? { year: n } : {};
}

export const Route = createFileRoute("/_authenticated/companies/$cnpj/")({
  validateSearch: (input: Record<string, unknown>): ParsedSearch => parseSearch(input),
  // ponytail: a stale URL the user lands on (bookmark, share, history)
  // — `/companies/27.093.558/0001-15`, `/companies/"27.093.558/0001-15"`,
  // anything with quotes or punctuation — would otherwise sit in the
  // address bar as-typed until the next navigation, because
  // validateSearch only normalizes the JS state. Re-emit the cleaned
  // path with `replace: true` so the address bar rewrites without
  // bloating the back stack. Same trick handles the formatted legacy
  // input during the compat window.
  beforeLoad: ({ params, search }) => {
    const raw = params.cnpj;
    const clean = toDigitsCnpj(raw);
    if (clean && clean !== raw) {
      // ponytail: rewrite only the path; preserve the search (year filter)
      // unchanged so a redirect from /companies/27.093.558/0001-15?year=2025
      // lands the user on /companies/27093558000115?year=2025.
      throw redirect({
        to: "/companies/$cnpj",
        params: { cnpj: clean },
        search,
        replace: true,
      });
    }
  },
  component: CompanyIndexRoute,
});

function CompanyIndexRoute() {
  const { cnpj: rawCnpj } = Route.useParams();
  const search = Route.useSearch();
  const cnpj = decodeURIComponent(rawCnpj);
  return <CompanyPage cnpj={cnpj} year={search.year} />;
}
